import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import queryString from 'query-string';
import Grid from '@material-ui/core/Grid';

import Forms from 'components/Forms';
import { useFormatMessage } from 'components/IntlProvider';
import { Button, BackTo } from '@deeptrue-frontend/ui';
import EmptyPlaceholder from 'components/EmptyPlaceholder/EmptyPlaceholder';
import { Dashboard } from 'components/Layout';
import api from 'api/api';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import { OrganizationUsersType } from 'store/modules/organizations/organizations';
import RouterProvider from 'components/RouterProvider/RouterProvider';
import Users from 'components/Users/Users';

const { ROUTES, Redirect } = RouterProvider;
const { connect, selectors, actions } = StoreProvider;

interface OrganizationUsersProps extends Omit<RouteComponentProps, 'match'> {
  users: OrganizationUsersType;
  setOrganizationsUsers: (payload: any) => void;
  isSuperAdmin: boolean;
  match: {
    params: {
      codename: string;
    };
  };
}

const OrganizationUsers = ({
  users,
  setOrganizationsUsers,
  match: { params },
  location,
  isSuperAdmin,
  history,
}: OrganizationUsersProps) => {
  const t = useFormatMessage();
  const [isLoaded, setIsLoaded] = useState(false);
  const [isAddUserModalVisible, setUserModalVisible] = useState(false);
  const parsedSearch = queryString.parse(location.search);

  const refreshUsersList = () =>
    api.resources
      .getOrganizationUsers({
        organization_name: params.codename,
        params: parsedSearch as {
          limit: string;
          offset: string;
          search: string;
        },
      })
      .then(({ data }) => {
        setOrganizationsUsers(data);
      })
      .finally(() => {
        setIsLoaded(true);
      });

  useEffect(() => {
    if (params.codename) {
      setIsLoaded(false);
      refreshUsersList();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  const getPageNumber = () => {
    if (parsedSearch.offset && !Array.isArray(parsedSearch.offset)) {
      return parseInt(parsedSearch.offset, 10) / 20;
    }

    return 0;
  };

  if (!params.codename) {
    return <Redirect to={ROUTES.ADMIN.ORGANIZATIONS.LIST} />;
  }

  return (
    <Dashboard
      title={`Organization ${params.codename} users`}
      location={location}
    >
      <BackTo onClick={() => history.push('/admin/organizations')}>
        {t('backTo.organizations', 'Back to Organizations')}
      </BackTo>
      <Grid
        style={{ marginLeft: 'auto' }}
        onClick={() => setUserModalVisible(true)}
      >
        <Button>{t('forms.inviteuser.add', 'Add users')}</Button>
      </Grid>

      <Forms.AddUserModal
        orgToInvite={params.codename}
        isVisible={isAddUserModalVisible}
        onClose={() => setUserModalVisible(false)}
      />

      <EmptyPlaceholder
        isLoaded={isLoaded}
        hasValues={users.list.length > 0}
        withLoader
      >
        <Users
          list={users.list}
          count={users.count}
          currentPage={getPageNumber()}
          refresh={refreshUsersList}
          isAdmin={isSuperAdmin}
        />
      </EmptyPlaceholder>
    </Dashboard>
  );
};

export default connect(
  (state) => ({
    users: selectors.organizationsSelectors.users(state),
    isSuperAdmin: selectors.authSelectors.isDeepTrueAdmin(state),
  }),
  {
    setOrganizationsUsers: actions.organizationsActions.setOrganizationsUsers,
  }
)(OrganizationUsers);
